import { Add, Check, Close, Remove } from "@mui/icons-material";
import {
  Box,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useMemo, useState, useRef, useEffect } from "react";
import { EFrequencyType, EWeekdays } from "../../../enums/event.enums";
import { IExclusionRule } from "../../../interfaces/event.interfaces";
import { menuPropsModal } from "../../../../../../../../utils";
import { FREQUENCY_OPTIONS } from "../consts/FrequencyOptions";
import { MonthlyDatePicker } from "../components/Monthly/MonthlyDatePicker";
import { WeekdaySelector } from "../components/Weekly/WeekdaySelector";
import { YearlyDatePicker } from "../components/Yearly/YearlyDatePicker";
import { IconButtonComponent } from "../../../../../../../common/IconButtonComponent";

// ─── Types ───────────────────────────────────────────────────────────────────

export type RepetitiveExecutionFormState = {
  frequencyType: EFrequencyType | "hourly" | "eachNDays" | "eachNMinutes" | "eachNHours";
  intervalValue: string;
  each: number;
  at: string; // "HH:mm"
  weekdays: EWeekdays[];
  monthDays: string[];
  yearDates: string[];
  endDate: string; // ISO with offset
  exclusions: IExclusionRule[];
  endAt?: string;
};

interface Props {
  initialValue: RepetitiveExecutionFormState;
  disabledForms?: string[];
  onApply: (next: RepetitiveExecutionFormState) => void;
  onClose: () => void;
  sx?: SxProps;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function formatSummary(state: RepetitiveExecutionFormState): string {
  const { frequencyType, intervalValue, each, weekdays, monthDays, yearDates, endDate } = state;
  const untilPart = endDate ? ` until ${DateTime.fromISO(endDate).toFormat("MMM dd, yyyy")}` : "";

  switch (frequencyType) {
    case EFrequencyType.DAILY:
      return capitalizeFirst(`every day${untilPart}`);
    case EFrequencyType.WEEKLY: {
      const days = weekdays.length ? weekdays.join(", ") : "no weekdays selected";
      const eachPart = each > 1 ? `every ${each} weeks` : "every week";
      return capitalizeFirst(`${eachPart} on ${days}${untilPart}`);
    }
    case EFrequencyType.MONTHLY: {
      const days = monthDays.length ? monthDays.join(", ") : "no days selected";
      const eachPart = each > 1 ? `every ${each} months` : "every month";
      return capitalizeFirst(`${eachPart} on day ${days}${untilPart}`);
    }
    case EFrequencyType.YEARLY: {
      const dates = yearDates.length ? yearDates.join(", ") : "no dates selected";
      return capitalizeFirst(`every year on ${dates}${untilPart}`);
    }
    case "eachNDays":
      return capitalizeFirst(`every ${Number(intervalValue || "1")} days${untilPart}`);
    case "eachNHours":
      return capitalizeFirst(`every ${Number(intervalValue || "1")} hours${untilPart}`);
    case "eachNMinutes":
      return capitalizeFirst(`every ${Number(intervalValue || "1")} minutes${untilPart}`);
    case "hourly":
      return capitalizeFirst(`every hour${untilPart}`);
    default:
      return capitalizeFirst(`Custom repeat${untilPart}`);
  }
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const SUBTITLE_SX = { color: "var(--fx-surface-texttertiary, #94A3B8)", fontWeight: 700, fontSize: 13, mb: 1 };

const STEPPER_CONTAINER_SX = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  background: "var(--fx-surface-sunken, #F8FAFC)",
  borderRadius: "8px",
  justifyContent: "space-between",
  px: 1,
};

// ─── Sub-components ───────────────────────────────────────────────────────────

interface IntervalStepperProps {
  value: string;
  onChange: (value: string) => void;
}

function IntervalStepper({ value, onChange }: IntervalStepperProps) {
  const decrement = () => {
    const next = Math.max(1, parseInt(value, 10) - 1);
    onChange(next.toString().padStart(2, "0"));
  };
  const increment = () => {
    const next = Math.min(99, parseInt(value, 10) + 1);
    onChange(next.toString().padStart(2, "0"));
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={SUBTITLE_SX}>
        Repeat every
      </Typography>
      <Box sx={{ ...STEPPER_CONTAINER_SX, width: 160 }}>
        <IconButton size="small" onClick={decrement} sx={{ color: "var(--fx-surface-text, #0F172A)" }}>
          <Remove fontSize="small" />
        </IconButton>
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: "var(--fx-surface-text, #0F172A)", textAlign: "center", width: 48 }}>
          {value.toString().padStart(2, "0")}
        </Typography>
        <IconButton size="small" onClick={increment} sx={{ color: "var(--fx-surface-text, #0F172A)" }}>
          <Add fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

interface EachStepperProps {
  value: number;
  label: string;
  onChange: (value: number) => void;
}

function EachStepper({ value, label, onChange }: EachStepperProps) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={SUBTITLE_SX}>
        Every
      </Typography>
      <Box sx={{ ...STEPPER_CONTAINER_SX, width: 240 }}>
        <IconButton size="small" onClick={() => onChange(Math.max(1, value - 1))} sx={{ color: "var(--fx-surface-text, #0F172A)" }}>
          <Remove fontSize="small" />
        </IconButton>
        <TextField
          value={value ?? 1}
          onChange={(e) => {
            const raw = e.target.value.replace(/\D/g, "");
            onChange(Math.min(99, Math.max(1, Number(raw || "1"))));
          }}
          size="small"
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            style: { textAlign: "center", width: 56, fontWeight: 700 },
          }}
          sx={{
            "& .MuiInputBase-root": {
              background: "var(--fx-surface-base, #FFFFFF)",
              borderRadius: "6px",
              height: 36,
            },
          }}
        />
        <IconButton size="small" onClick={() => onChange(Math.min(99, value + 1))} sx={{ color: "var(--fx-surface-text, #0F172A)" }}>
          <Add fontSize="small" />
        </IconButton>
        <Typography sx={{ pr: 1, fontSize: 14, color: "var(--fx-surface-text, #0F172A)" }}>{label}</Typography>
      </Box>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const CustomRecurrencyModalContent = ({ initialValue, disabledForms, onApply, onClose, sx }: Props) => {
  const [draft, setDraft] = useState<RepetitiveExecutionFormState>(() => ({
    ...initialValue,
    intervalValue: initialValue.intervalValue ?? "01",
    each: initialValue.each ?? 1,
    at: initialValue.at ?? "09:30",
    weekdays: initialValue.weekdays ?? [],
    monthDays: initialValue.monthDays ?? [],
    yearDates: initialValue.yearDates ?? [],
    exclusions: initialValue.exclusions ?? [],
  }));

  const customFrequencyTypeRef = useRef<string | null>(null);

  const standardOptions = useMemo(
    () => FREQUENCY_OPTIONS.filter((opt) => !disabledForms?.includes(opt.value)),
    [disabledForms]
  );

  const isCurrentValueCustom = useMemo(
    () => !standardOptions.some((opt) => opt.value === draft.frequencyType),
    [draft.frequencyType, standardOptions]
  );

  useEffect(() => {
    if (isCurrentValueCustom && draft.frequencyType) {
      customFrequencyTypeRef.current = draft.frequencyType;
    }
  }, [isCurrentValueCustom, draft.frequencyType]);

  const availableOptions = useMemo(() => {
    const customValue = customFrequencyTypeRef.current ?? (isCurrentValueCustom ? draft.frequencyType : null);
    if (customValue && !standardOptions.some((opt) => opt.value === customValue)) {
      return [...standardOptions, { value: customValue, label: "Custom" }];
    }
    return standardOptions;
  }, [draft.frequencyType, standardOptions, isCurrentValueCustom]);

  const summary = useMemo(() => formatSummary(draft), [draft]);

  const showIntervalField = ["eachNMinutes", "eachNHours", "eachNDays"].includes(draft.frequencyType);
  const showEach = draft.frequencyType === EFrequencyType.WEEKLY || draft.frequencyType === EFrequencyType.MONTHLY;
  const eachLabel = draft.frequencyType === EFrequencyType.WEEKLY ? "Weeks" : "Months";
  const isYearly = draft.frequencyType === EFrequencyType.YEARLY;

  const hasSelection = useMemo(() => {
    if (draft.frequencyType === EFrequencyType.WEEKLY) return draft.weekdays.length > 0;
    if (draft.frequencyType === EFrequencyType.MONTHLY) return draft.monthDays.length > 0;
    if (draft.frequencyType === EFrequencyType.YEARLY) return draft.yearDates.length > 0;
    return true;
  }, [draft.frequencyType, draft.weekdays, draft.monthDays, draft.yearDates]);

  const handleField = (field: keyof RepetitiveExecutionFormState, value: any) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleDone = () => {
    onApply(draft);
    onClose();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 4,
        bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
        overflowY: "hidden",
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ fontSize: 14.5 }}>{summary}</Typography>
        <Typography sx={{ fontSize: 12, color: "var(--fx-surface-textsecondary, #64748B)" }}>
          Time is set in the schedule options (Fixed/Buffered).
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2} alignItems="flex-start">
        {/* Frequency type selector */}
        <Grid item xs={12} md={isYearly ? 12 : 6}>
          <Typography variant="subtitle2" sx={SUBTITLE_SX}>
            Recurrency by
          </Typography>
          <FormControl size="small" fullWidth>
            <Select
              MenuProps={menuPropsModal}
              value={draft.frequencyType}
              onChange={(e) => handleField("frequencyType", e.target.value)}
              renderValue={(selected) => {
                const isCustom = !standardOptions.some((opt) => opt.value === selected);
                if (isCustom) return formatSummary(draft);
                return availableOptions.find((opt) => opt.value === selected)?.label ?? selected;
              }}
              sx={{
                width: "100%",
                background: "var(--fx-surface-sunken, #F8FAFC)",
                borderRadius: "8px",
                "& .MuiSelect-select": { padding: "8px 12px", fontSize: 15, color: "var(--fx-surface-text, #0F172A)" },
              }}
            >
              {availableOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Interval / Each stepper */}
        {!isYearly && (
          <Grid item xs={12} md={6}>
            {showIntervalField ? (
              <IntervalStepper
                value={draft.intervalValue}
                onChange={(v) => handleField("intervalValue", v)}
              />
            ) : showEach ? (
              <EachStepper
                value={draft.each}
                label={eachLabel}
                onChange={(v) => handleField("each", v)}
              />
            ) : (
              <Box>
                <Typography variant="subtitle2" sx={SUBTITLE_SX}>
                  Every
                </Typography>
                <Box sx={{ background: "var(--fx-surface-sunken, #F8FAFC)", borderRadius: "8px", px: 1.5, py: 1.25, color: "var(--fx-surface-texttertiary, #94A3B8)", fontSize: 13 }}>
                  —
                </Box>
              </Box>
            )}
          </Grid>
        )}
      </Grid>

      {/* Context-specific day/date selectors */}
      {draft.frequencyType === EFrequencyType.WEEKLY && (
        <Box sx={{ mt: -1 }}>
          <WeekdaySelector value={draft.weekdays} onChange={(v) => handleField("weekdays", v)} />
        </Box>
      )}
      {draft.frequencyType === EFrequencyType.MONTHLY && (
        <Box sx={{ mt: -1 }}>
          <MonthlyDatePicker value={draft.monthDays} onChange={(v) => handleField("monthDays", v)} />
        </Box>
      )}
      {draft.frequencyType === EFrequencyType.YEARLY && (
        <Box sx={{ mt: -1 }}>
          <YearlyDatePicker value={draft.yearDates} onChange={(v) => handleField("yearDates", v)} />
        </Box>
      )}

      {hasSelection && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            mt: 3,
            pt: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
            zIndex: 1,
          }}
        >
          <IconButtonComponent onClick={onClose} theme="grey" sx={{ width: 40, height: 40 }} sxIcon={{}} disabled={false}>
            <Close />
          </IconButtonComponent>
          <IconButtonComponent onClick={handleDone} theme="blue" sx={{ width: 40, height: 40 }} sxIcon={{}} disabled={false}>
            <Check />
          </IconButtonComponent>
        </Box>
      )}
    </Box>
  );
};